import { useMemo, useState } from 'react';
import { Device, DeviceBattery } from '@/api';
import { UIMultiFilterGroup } from '@/ui';
import { search } from '@/common/util/search';
import { deriveDeviceFeatureStates } from '../util/device-feature-states';
import { useDevicesTranslation } from './use-devices-translation';

type BatteryFilter = 'all' | 'low' | 'good';

export type UseDevicesSearchResult = {
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    searchedDevices: Device[];
    clearSearch: () => void;
    groups: UIMultiFilterGroup[];
    selectedCount: number;
    onFiltersChanged: (newGroups: UIMultiFilterGroup[]) => void;
};

export function useDevicesSearch(
    devices: Device[],
    options?: { hideTeam?: boolean }
): UseDevicesSearchResult {
    const { t } = useDevicesTranslation();
    const hideTeam = options?.hideTeam ?? false;

    const [searchQuery, setSearchQuery] = useState('');
    const [batteryFilter, setBatteryFilter] = useState<BatteryFilter>('all');
    const [featuresFilter, setFeaturesFilter] = useState<Map<string, boolean>>(
        new Map()
    );
    const [selectedTeams, setSelectedTeams] = useState<Map<string, string>>(
        new Map()
    );
    const [pendingOnly, setPendingOnly] = useState(false);

    const selectedCount = useMemo(() => {
        let count = 0;
        if (batteryFilter !== 'all') {
            count++;
        }

        featuresFilter.forEach((value) => {
            if (value) {
                count++;
            }
        });

        if (pendingOnly) {
            count++;
        }

        count += selectedTeams.size;

        return count;
    }, [batteryFilter, featuresFilter, pendingOnly, selectedTeams]);

    function clearSearch() {
        setSearchQuery('');
        setBatteryFilter('all');
        setFeaturesFilter(new Map());
        setSelectedTeams(new Map());
        setPendingOnly(false);
    }

    const allTeamOptions = useMemo(() => {
        const teams = new Map<string, string>();
        devices.forEach((device) => {
            if (device.team) {
                teams.set(
                    device.team.teamId,
                    device.team.name ?? t('shared.devices.unknown_team')
                );
            } else {
                teams.set('no-team', t('shared.devices.no_team'));
            }
        });
        return teams;
    }, [devices, t]);

    const groups: UIMultiFilterGroup[] = useMemo(() => {
        const items: UIMultiFilterGroup[] = [
            {
                name: t('shared.filters.battery'),
                type: 'single',
                items: [
                    {
                        name: t('shared.filters.battery_all'),
                        id: 'all',
                        selected: batteryFilter === 'all'
                    },
                    {
                        name: t('shared.filters.battery_low'),
                        id: 'low',
                        selected: batteryFilter === 'low'
                    },
                    {
                        name: t('shared.filters.battery_good'),
                        id: 'good',
                        selected: batteryFilter === 'good'
                    }
                ]
            },
            {
                name: t('shared.devices.features_label'),
                type: 'multi',
                items: [
                    {
                        name: t('components.devices_list.filter_fall_alerts'),
                        id: 'fall',
                        selected: featuresFilter.get('fall') ?? false
                    },
                    {
                        name: t('components.devices_list.filter_tilt_alerts'),
                        id: 'tilt',
                        selected: featuresFilter.get('tilt') ?? false
                    },
                    {
                        name: t(
                            'components.devices_list.filter_geofence_alerts'
                        ),
                        id: 'geofence',
                        selected: featuresFilter.get('geofence') ?? false
                    },
                    {
                        name: t('shared.devices.wifi_label'),
                        id: 'wifi',
                        selected: featuresFilter.get('wifi') ?? false
                    }
                ]
            },
            {
                name: t('shared.labels.pending'),
                type: 'multi',
                items: [
                    {
                        name: t(
                            'components.devices_list.filter_pending_changes'
                        ),
                        id: 'pending',
                        selected: pendingOnly
                    }
                ]
            }
        ];

        if (!hideTeam && allTeamOptions.size > 0) {
            // sort so that no team is always top
            items.push({
                name: t('components.devices_list.filter_teams'),
                type: 'multi',
                items: Array.from(allTeamOptions)
                    .sort(([teamId, teamName], [teamId2, teamName2]) => {
                        if (teamId === 'no-team') {
                            return -1;
                        }
                        if (teamId2 === 'no-team') {
                            return 1;
                        }
                        // Now sort alphabetically
                        if (teamName < teamName2) {
                            return -1;
                        }
                        if (teamName > teamName2) {
                            return 1;
                        }
                        return 0;
                    })
                    .map(([teamId, teamName]) => ({
                        name: teamName,
                        id: teamId ?? 'no-team',
                        selected: selectedTeams.has(teamId)
                    }))
            });
        }

        return items;
    }, [
        batteryFilter,
        featuresFilter,
        pendingOnly,
        allTeamOptions,
        selectedTeams,
        hideTeam,
        t
    ]);

    function onFiltersChanged(newGroups: UIMultiFilterGroup[]) {
        // Battery is always index 0
        const newSelectedBatteryFilter = newGroups[0].items.find(
            (item) => item.selected
        )?.id as BatteryFilter;
        setBatteryFilter(newSelectedBatteryFilter);

        // Features is always index 1
        const newSelectedFeatures = new Map<string, boolean>();
        newGroups[1].items.forEach((item) => {
            newSelectedFeatures.set(item.id, item.selected);
        });
        setFeaturesFilter(newSelectedFeatures);

        // Pending is always index 2
        setPendingOnly(
            newGroups[2].items.find((item) => item.id === 'pending')
                ?.selected ?? false
        );

        // Teams is index 3 (if present)
        if (newGroups[3]) {
            const newSelectedTeams = new Map<string, string>();
            newGroups[3].items.forEach((item) => {
                if (item.selected) {
                    newSelectedTeams.set(item.id, item.name);
                }
            });
            setSelectedTeams(newSelectedTeams);
        }
    }

    const filteredDevices = useMemo(() => {
        return devices.filter((device) => {
            // Battery
            if (batteryFilter === 'low') {
                if (!device.battery || !isBatteryLow(device.battery)) {
                    return false;
                }
            }
            if (batteryFilter === 'good') {
                if (!device.battery || !isBatteryGood(device.battery)) {
                    return false;
                }
            }

            // Features — match the effective state shown in the Features
            // column (enabled, or pending a change), not the stale flags.
            const featureStates = deriveDeviceFeatureStates(device);
            const hasFeature = (state: {
                enabled: boolean;
                pending: boolean;
            }) => state.enabled || state.pending;
            if (
                (featuresFilter.get('fall') &&
                    !hasFeature(featureStates.fall)) ||
                (featuresFilter.get('tilt') &&
                    !hasFeature(featureStates.tilt)) ||
                (featuresFilter.get('geofence') &&
                    !hasFeature(featureStates.geofence)) ||
                (featuresFilter.get('wifi') && !hasFeature(featureStates.wifi))
            ) {
                return false;
            }

            // Pending — only devices with an in-flight command
            if (
                pendingOnly &&
                !device.commands?.some((c) => c.status === 'pending')
            ) {
                return false;
            }

            // Teams
            if (selectedTeams.size > 0) {
                const searchTeamId = device.team?.teamId ?? 'no-team';
                if (!selectedTeams.has(searchTeamId)) {
                    return false;
                }
            }

            return true;
        });
    }, [devices, batteryFilter, featuresFilter, pendingOnly, selectedTeams]);

    const searchedDevices = useMemo(() => {
        const q = searchQuery.trim();
        if (!q) {
            return filteredDevices;
        }

        // A scanned (or pasted) full device id must resolve to exactly one
        // device; fuzzy matching can otherwise surface several devices with
        // similar ids. Names/teams/users stay fuzzy below; id is intentionally
        // excluded so partial or near-identical ids never fuzzy-match.
        const exact = filteredDevices.find((d) => d.id === q);
        if (exact) {
            return [exact];
        }

        return search(q, filteredDevices, [
            'name',
            {
                name: 'teamName',
                getFn: (item) => (item.team ? (item.team.name ?? '') : '')
            },
            {
                name: 'userName',
                getFn: (item) =>
                    item.user
                        ? `${item.user.firstName ?? ''} ${item.user.lastName ?? ''}`
                        : ''
            }
        ]);
    }, [filteredDevices, searchQuery]);

    return {
        searchQuery,
        setSearchQuery,
        searchedDevices,
        clearSearch,
        groups,
        selectedCount,
        onFiltersChanged
    };
}

/**
 * Helpers
 */
function isBatteryGood(battery: DeviceBattery): boolean {
    return battery.level >= 10;
}

function isBatteryLow(battery: DeviceBattery): boolean {
    return battery.level < 10;
}
